import { OptionItem } from '@/contracts/models/survey';
import { useTranslation } from '@/hooks/useTranslation';
import React from 'react';
import { ReorderableList } from './ReorderableList';

export interface MatrixSelectorProps {
  rows: OptionItem[];
  columns: OptionItem[];
  onRowsChange: (rows: OptionItem[]) => void;
  onColumnsChange: (columns: OptionItem[]) => void;
  disabled?: boolean;
  lockStructure?: boolean;
  originalRows?: string[];
  originalColumns?: string[];
}

export const MatrixSelector: React.FC<MatrixSelectorProps> = ({
  rows,
  columns,
  onRowsChange,
  onColumnsChange,
  disabled = false,
  lockStructure = false,
  originalRows,
  originalColumns,
}) => {
  const { t } = useTranslation();

  return (
    <div className='space-y-4'>
      {/* Rows Section */}
      <div>
        <label className='text-sm font-medium text-gray-700 block mb-2'>
          {t('editor.fields.matrixRows')}
        </label>
        <ReorderableList
          items={rows}
          onChange={onRowsChange}
          disabled={disabled}
          lockStructure={lockStructure}
          originalItems={originalRows}
          minItems={1}
          itemType='row'
          t={t}
          getItemPlaceholder={index =>
            t('editor.fields.defaultRowName', { number: index + 1 })
          }
        />
      </div>

      {/* Columns Section */}
      <div>
        <label className='text-sm font-medium text-gray-700 block mb-2'>
          {t('editor.fields.matrixColumns')}
        </label>
        <ReorderableList
          items={columns}
          onChange={onColumnsChange}
          disabled={disabled}
          lockStructure={lockStructure}
          originalItems={originalColumns}
          minItems={2}
          itemType='column'
          t={t}
          getItemPlaceholder={index =>
            t('editor.fields.defaultColumnName', { number: index + 1 })
          }
        />
      </div>

      {/* Matrix Preview — hidden when structure is locked */}
      {!lockStructure && (
        <div className='mt-4 p-4 bg-gray-50 rounded-lg'>
          <h3 className='text-sm font-medium text-gray-700 mb-2'>
            {t('editor.fields.matrixPreview')}
          </h3>
          <div className='overflow-x-auto'>
            <table className='w-full text-sm border border-gray-200'>
              <thead>
                <tr className='bg-gray-100'>
                  <th className='border border-gray-200 p-2 text-left'>
                    <span className='sr-only'>
                      {t('editor.fields.matrixRowHeader')}
                    </span>
                  </th>
                  {columns.map((column, index) => (
                    <th
                      key={column.value}
                      className='border border-gray-200 p-2 text-center'
                    >
                      {column.text ||
                        t('editor.fields.defaultColumnName', {
                          number: index + 1,
                        })}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {rows.map((row, rowIndex) => (
                  <tr key={row.value}>
                    <td className='border border-gray-200 p-2 font-medium'>
                      {row.text ||
                        t('editor.fields.defaultRowName', {
                          number: rowIndex + 1,
                        })}
                    </td>
                    {columns.map((column, columnIndex) => (
                      <td
                        key={column.value}
                        className='border border-gray-200 p-2 text-center'
                      >
                        <input
                          type='radio'
                          name={`matrix-${rowIndex}`}
                          disabled
                          className='opacity-50'
                          aria-label={t('editor.fields.matrixCellAriaLabel', {
                            row:
                              row.text ||
                              t('editor.fields.defaultRowName', {
                                number: rowIndex + 1,
                              }),
                            column:
                              column.text ||
                              t('editor.fields.defaultColumnName', {
                                number: columnIndex + 1,
                              }),
                          })}
                        />
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
};
